import React, { useState, useEffect } from 'react';
import { clsx } from 'clsx';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { useLoader } from 'src/provider/LoaderProvider';
import PageContentWrapper from '../../components/PageContentWrapper/PageContentWrapper';
import CardContainer from '../../components/CardContainer/CardContainer';
import { ConversationFilter } from 'src/pages/ConversationsPage/ConversationFilter/ConversationFilter';
import { useLazyGetProjectChatsQuery } from 'src/services';
import { ConversationChips } from 'src/pages/ConversationsPage/ConversationChips/ConversationChips';
import { ConversationList } from 'src/pages/ConversationsPage/ConversationList/ConversationList';
import { ConversationChat } from 'src/pages/ConversationsPage/ConversationChat/ConversationChat';
import { ConversationSubject } from 'src/pages/ConversationsPage/ConversationSubject/ConversationSubject';
import { EmptyChatIcon } from 'src/pages/ConversationsPage/ConversationChat/EmptyChatIcon';
import { useGetAccountsQuery } from 'src/services/accountsService';
import type { ChatsRequest, ChatsRequestChange } from 'src/pages/ConversationsPage/chatsRequest';
import { conversationKey, type Conversation } from 'src/pages/ConversationsPage/conversationTypes';

const Counter = ({
  items = null,
  children,
}: {
  items?: unknown[] | null;
  children?: React.ReactNode;
}) => (
  <div
    className={'text-tiny text-ebony font-semibold font-jamjuree py-[3px] px-[7px] bg-[#EEECFD]'}
  >
    {items ? items.length : children}
  </div>
);

const initialRequest: ChatsRequest = {
  project_id: null,
  service: 'customer',
  created_at_start: '',
  created_at_end: '',
  updated_at_start: '',
  updated_at_end: '',
  page: 1,
  page_size: 20,
  chat_status: ['open', 'escalated', 'closed', 'auto_closed'],
  escalation_reason_category: [],
  account: '',
  types: ['chat'],
  order_by: ['-updated_at'],
};

// Whether the keys both have (except omitProps) hold the same JSON.
const compare = (object1: ChatsRequest, object2: ChatsRequestChange, omitProps: string[] = []) => {
  for (const property in object2) {
    const key = property as keyof ChatsRequest;

    if (!omitProps.includes(property) && object1.hasOwnProperty(property)) {
      if (JSON.stringify(object1[key]) !== JSON.stringify(object2[key])) {
        return false;
      }
    }
  }
  return true;
};

const ConversationsPage = () => {
  const { id } = useParams();
  const loader = useLoader();
  const [request, setRequest] = useState<ChatsRequest | null>(null);
  const [selectedChat, setSelectedChat] = useState<Conversation | null>(null);
  const [chats, setChats] = useState<Conversation[]>([]);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [fetchChats, { data: chatsData, isLoading, isFetching, isError }] =
    useLazyGetProjectChatsQuery();
  const { data: account } = useGetAccountsQuery();

  useEffect(() => {
    if (isError) {
      loader.hide();
    }
  }, [isError]);

  // The unfiltered list: trial plans also see their playground conversations.
  const defaultRequest: ChatsRequest | null = account && {
    ...initialRequest,
    project_id: id,
    types: account.general_plan?.plan?.plan_type === 'trial' ? ['playground', 'chat'] : ['chat'],
  };

  // Start from the defaults once the account is known, and again for another project. A later
  // account refetch keeps the filters and the pages already loaded.
  useEffect(() => {
    if (!account || request?.project_id === id) {
      return;
    }

    if (request) {
      setChats([]);
      setSelectedChat(null);
      setHasNextPage(false);
    }

    setRequest(defaultRequest);
  }, [account, id]);

  useEffect(() => {
    if (request) {
      fetchChats(request);
    }
  }, [request]);

  useEffect(() => {
    if (chatsData) {
      const newChats = chatsData.results
        .map((chat) => {
          return {
            ...chat,
            selected: false,
          };
        })
        .filter((chat) => chat.messages.length > 0);

      // Pages are by offset, so a chat updated between page loads pushes the others down and the
      // next page repeats chats already listed; keep the copies already shown.
      const listed = new Set(chats.map(conversationKey));
      setChats([...chats, ...newChats.filter((chat) => !listed.has(conversationKey(chat)))]);

      if (
        !chatsData.results.length ||
        !chatsData.pagination.total_pages ||
        chatsData.pagination.current_page === chatsData.pagination.total_pages
      ) {
        setHasNextPage(false);
      } else {
        setHasNextPage(true);
      }
    }
  }, [chatsData]);

  const onFilterChange = (data: ChatsRequestChange) => {
    const isEqual = compare(request, data);

    if (!isEqual) {
      setChats([]);
      setSelectedChat(null);
      setHasNextPage(false);

      setRequest({
        ...request,
        ...data,
        page: 1,
      });
    }
  };

  const onChipsChange = (data: ChatsRequestChange) => {
    setChats([]);
    setSelectedChat(null);
    setHasNextPage(false);

    return setRequest({
      ...request,
      ...data,
      page: 1,
    });
  };

  const loadMore = () => {
    if (!isFetching && !isLoading && request) {
      setRequest({
        ...request,
        page: request.page + 1,
      });
    }
  };

  return (
    <PageContentWrapper className={clsx('h-[calc(100%-70px)]')}>
      <div className="z-10 gap-[10px] flex flex-row relative h-full overflow-hidden">
        <CardContainer className="flex-1 min-w-0.5 relative">
          <div className="card-header !min-h-[auto]">
            <div className={'flex flex-row gap-[0.625rem] items-center'}>
              <Counter>{chatsData?.pagination?.total_items || 0}</Counter>
              <span className={'text-base-sm text-ebony font-bold max-lg:hidden'}>
                Conversations
              </span>
            </div>
            <div className={'flex flex-row gap-10'}>
              <div className={'w-8.5 h-8.5'}>{/*<SearchInput />*/}</div>
            </div>
            <ConversationFilter
              request={request}
              defaultRequest={defaultRequest}
              onChange={onFilterChange}
            />
          </div>
          <ConversationChips request={request} onChange={onChipsChange} />
          <div className="card-body overflow-hidden">
            <ConversationList
              isLoading={isLoading}
              isFetching={isFetching}
              isError={isError}
              loadMore={loadMore}
              chats={chats}
              hasNextPage={hasNextPage}
              onSelect={setSelectedChat}
            />
          </div>
        </CardContainer>
        <CardContainer className="flex-1 min-w-1">
          <div className="!flex-col">
            <div className={'flex flex-row justify-between items-center w-full'}>
              <ConversationSubject chat={selectedChat} />
            </div>
            {selectedChat && (
              <div className={'flex flex-row items-center w-full gap-2.5 mt-2.5'}>
                <p className={'text-xs font-medium text-ebony_opacity_50'}>
                  <span className={'font-semibold'}>Date Created: </span>
                  <span>{moment(selectedChat?.created_at).format('lll')}</span>
                </p>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="4"
                  height="4"
                  viewBox="0 0 4 4"
                  fill="none"
                >
                  <circle cx="2" cy="2" r="2" fill="#05050D" fillOpacity="0.5" />
                </svg>
                <p className={'text-xs font-medium text-ebony_opacity_50'}>
                  <span className={'font-semibold'}>Last Updated: </span>
                  <span>{moment(selectedChat?.updated_at).format('lll')}</span>
                </p>
              </div>
            )}
          </div>
          <div
            className={clsx(
              'card-body h-full max-h-[636px] overflow-hidden',
              !selectedChat && 'flex h-full max-h-full items-center justify-center',
            )}
          >
            {selectedChat ? <ConversationChat chat={selectedChat} /> : <EmptyChatIcon />}
          </div>
        </CardContainer>
      </div>
    </PageContentWrapper>
  );
};

export default ConversationsPage;
